import React, { useEffect, useState } from "react";
import {
  sourceSchema,
  expression_parseFromSource,
} from "./surveyAdapterMicrosite.const";
import Ajv from "ajv";
import jsonata from "jsonata";
import { useSelector } from "react-redux";
import { LANGUAGES } from "../../constants/supported_lang";
const getLanguageId = (languageCode: any) => {
  const language = LANGUAGES.find((lang) => lang.code === languageCode);
  return language ? language.id : null; // Return the id or null if not found
};
export const useSurveyAdapterMicrosite = () => {
  const ajv = new Ajv();
  const [sourceData, setSourceData] = useState<any>();
  const appUiState = useSelector((state: any) => state.portal.uiState);
  const appLanguage = appUiState?.Language;
  const languageId = getLanguageId(appLanguage);
  let languageData = localStorage.getItem("languageData");
  const parseFromSource = async (sourceData: any) => {
    const validate = ajv.compile(sourceSchema);
    const valid = validate(sourceData);
    if (valid) {
      setSourceData(sourceData);
      const expression = jsonata(expression_parseFromSource);
      let parsedRecords = await expression.evaluate(sourceData);
      return parsedRecords;
    } else {
      return await false;
    }
  };
  const parseToSource = async (parsedData: any, isPartial = false) => {
    // Increase delay for better iOS compatibility
    const delay = () => new Promise((resolve) => setTimeout(resolve, 50));

    // Create a safe copy of parsedData to avoid reference issues in Safari/iOS
    const safeData = JSON.parse(JSON.stringify(parsedData));

    // Use traditional for loops for better Safari/iOS compatibility
    if (safeData && safeData.pages) {
      for (let pIndex = 0; pIndex < safeData.pages.length; pIndex++) {
        const p = safeData.pages[pIndex];
        if (p && p.questionGroups) {
          for (let gIndex = 0; gIndex < p.questionGroups.length; gIndex++) {
            const qg = p.questionGroups[gIndex];
            if (qg && qg.questions) {
              for (let qIndex = 0; qIndex < qg.questions.length; qIndex++) {
                const q = qg.questions[qIndex];
                if (q) {
                  syncQuestionChanges(q);
                }
              }
            }
          }
        }
      }
    }

    await delay();

    // Deep clone sourceData to avoid reference mutation issues
    const safeSourceData = JSON.parse(JSON.stringify(sourceData));
    let finalData = {};

    if (isPartial) {
      // Handle partial data scenario
      const pageNumbers: any[] = [];
      if (safeData && Array.isArray(safeData.pages)) {
        for (let i = 0; i < safeData.pages.length; i++) {
          const pg = safeData.pages[i];
          if (pg && pg.pageNumber !== undefined) {
            pageNumbers.push(pg.pageNumber);
          }
        }
      }

      let filteredDetails = [];
      if (safeSourceData && Array.isArray(safeSourceData.PatientActivityDetails)) {
        filteredDetails = safeSourceData.PatientActivityDetails.filter(function (question: { PageNumber: any; }) {
          return question && pageNumbers.includes(question.PageNumber);
        });
      }

      finalData = {
        ...safeSourceData,
        LastUpdatedPageNumber: safeData.lastUpdatedPageNumber,
        PreferredLanguage: languageId,
        PatientActivityDetails: []
      };

      // Process each question in the filtered details
      finalData.PatientActivityDetails = filteredDetails.map(function (question: any) {
        // Create a safe copy
        const questionCopy = { ...question };

        // Safely process choices
        const transformedChoices = [];
        if (Array.isArray(questionCopy.Choices)) {
          for (let i = 0; i < questionCopy.Choices.length; i++) {
            const choice = questionCopy.Choices[i];
            if (choice && choice.choiceText) {
              transformedChoices.push(choice.choiceText);
            }
          }
        }

        // Safely handle EnglishChoice
        let englishChoiceString = '';
        if (Array.isArray(questionCopy.EnglishChoice)) {
          const validItems = questionCopy.EnglishChoice.filter((item: null | undefined) => item !== undefined && item !== null);
          englishChoiceString = validItems.join(questionCopy.FieldDelimiter || ',');
        }

        questionCopy.EnglishChoice = [englishChoiceString];

        // Fix for Safari: use proper method to get englishtransformedChoices
        const englishtransformedChoices = englishChoiceString.split(questionCopy.FieldDelimiter || ',');

        let englishResponse = "";
        if (questionCopy.QuestionType == 120000001) {
          // Safely handle response for multi-select
          const responseArray = Array.isArray(questionCopy.Response) ?
            questionCopy.Response.filter((item: any) => item && typeof item === 'object') : [];

          // Use traditional loops for finding indices to avoid Safari issues
          const matchedIndices = [];
          for (let i = 0; i < responseArray.length; i++) {
            const responseItem = responseArray[i];
            if (!responseItem || !responseItem.value) continue;

            let foundIndex = -1;
            if (Array.isArray(questionCopy.Choices)) {
              for (let j = 0; j < questionCopy.Choices.length; j++) {
                const choice = questionCopy.Choices[j];
                if (choice && choice.choiceText === responseItem.value) {
                  foundIndex = j;
                  break;
                }
              }
            }

            if (foundIndex !== -1) {
              matchedIndices.push(foundIndex);
            }
          }

          if (matchedIndices.length > 0) {
            const responseStrings = [];
            for (let i = 0; i < matchedIndices.length; i++) {
              const index = matchedIndices[i];
              if (index >= 0 && index < englishtransformedChoices.length) {
                responseStrings.push(englishtransformedChoices[index]);
              }
            }
            englishResponse = responseStrings.join(questionCopy.FieldDelimiter || ',');
          } else {
            englishResponse = questionCopy.ResponseText || "";
          }

          // Handle "None of the above" option
          let responseData = [];
          if (Array.isArray(questionCopy.Response)) {
            for (let i = 0; i < questionCopy.Response.length; i++) {
              const item = questionCopy.Response[i];
              if (item && item.Selected && item.value) {
                responseData.push(item.value);
              }
            }
          }

          if (
            questionCopy.IsNoneOfTheAboveEnabled &&
            questionCopy.NoneOfTheAboveText &&
            responseData.includes(questionCopy.NoneOfTheAboveText)
          ) {
            if (languageData) {
              try {
                // Create a local copy to avoid modifying the original
                let langData = languageData;
                if (typeof langData === 'string') {
                  langData = JSON.parse(langData);
                }

                if (Array.isArray(langData)) {
                  const engDataJson = langData.filter(item =>
                    item && item.LanguageCode === 120000000
                  ) || [];

                  let englishChoicesMatch = null;
                  for (let i = 0; i < engDataJson.length; i++) {
                    const item = engDataJson[i];
                    if (item && item.TextCode === questionCopy.NoneOfTheAboveTextCode) {
                      englishChoicesMatch = item;
                      break;
                    }
                  }

                  if (englishChoicesMatch && englishChoicesMatch.TextValue) {
                    englishResponse = englishChoicesMatch.TextValue;
                  }
                } else {
                  console.error("languageData is not an array:", langData);
                }
              } catch (error) {
                console.error("Error handling languageData:", error);
              }
            }
          }

          // Handle "Other" option
          if (questionCopy.IsOtherEnabled && questionCopy.OtherResponse) {
            if (englishResponse) {
              englishResponse += `${questionCopy.FieldDelimiter || ','} ${questionCopy.OtherResponse}`;
            } else {
              englishResponse = questionCopy.OtherResponse;
            }
          }
        } else {
          // Single select question handling
          let matchedIndex = -1;
          if (Array.isArray(questionCopy.Choices) && questionCopy.Response) {
            for (let i = 0; i < questionCopy.Choices.length; i++) {
              const choice = questionCopy.Choices[i];
              if (choice && choice.choiceText === questionCopy.Response) {
                matchedIndex = i;
                break;
              }
            }
          }

          if (matchedIndex !== -1 && matchedIndex < englishtransformedChoices.length) {
            englishResponse = englishtransformedChoices[matchedIndex];
          } else if (questionCopy.ResponseText) {
            englishResponse = questionCopy.ResponseText;
          } else if (questionCopy.OtherResponse) {
            englishResponse = questionCopy.OtherResponse;
          } else {
            englishResponse = "";
          }
        }

        return {
          ...questionCopy,
          Choices: [transformedChoices.join(questionCopy.FieldDelimiter || ',')],
          EnglishResponse: englishResponse,
          formattedChoicesEnglish: englishtransformedChoices,
        };
      }).filter(Boolean); // Filter out any undefined results
    } else {
      // Handle full data scenario
      finalData = {
        ...safeSourceData,
        PreferredLanguage: languageId,
        PatientActivityDetails: []
      };

      // Process all questions
      if (safeSourceData && Array.isArray(safeSourceData.PatientActivityDetails)) {
        finalData.PatientActivityDetails = safeSourceData.PatientActivityDetails.map(
          (question: any) => {
            // Create a safe copy
            const questionCopy = { ...question };

            // Safely process choices
            const transformedChoices = [];
            if (Array.isArray(questionCopy.Choices)) {
              for (let i = 0; i < questionCopy.Choices.length; i++) {
                const choice = questionCopy.Choices[i];
                if (choice && choice.choiceText) {
                  transformedChoices.push(choice.choiceText);
                }
              }
            }

            // Safely handle EnglishChoice
            let englishChoiceString = '';
            if (Array.isArray(questionCopy.EnglishChoice)) {
              const validItems = questionCopy.EnglishChoice.filter((item: null | undefined) => item !== undefined && item !== null);
              englishChoiceString = validItems.join(questionCopy.FieldDelimiter || ',');
            }

            questionCopy.EnglishChoice = [englishChoiceString];

            // Fix for Safari: use proper method to get englishtransformedChoices
            const englishtransformedChoices = englishChoiceString.split(questionCopy.FieldDelimiter || ',');

            let englishResponse = "";

            if (questionCopy.QuestionType === 120000001) {
              // Safely handle response for multi-select
              const responseArray = Array.isArray(questionCopy.Response) ?
                questionCopy.Response.filter((item: any) => item && typeof item === 'object') : [];

              // Use traditional loops for finding indices to avoid Safari issues
              const matchedIndices = [];
              for (let i = 0; i < responseArray.length; i++) {
                const responseItem = responseArray[i];
                if (!responseItem || !responseItem.value) continue;

                let foundIndex = -1;
                if (Array.isArray(questionCopy.Choices)) {
                  for (let j = 0; j < questionCopy.Choices.length; j++) {
                    const choice = questionCopy.Choices[j];
                    if (choice && choice.choiceText === responseItem.value) {
                      foundIndex = j;
                      break;
                    }
                  }
                }

                if (foundIndex !== -1) {
                  matchedIndices.push(foundIndex);
                }
              }

              if (matchedIndices.length > 0) {
                const responseStrings = [];
                for (let i = 0; i < matchedIndices.length; i++) {
                  const index = matchedIndices[i];
                  if (index >= 0 && index < englishtransformedChoices.length) {
                    responseStrings.push(englishtransformedChoices[index]);
                  }
                }
                englishResponse = responseStrings.join(questionCopy.FieldDelimiter || ',');
              } else {
                englishResponse = questionCopy.ResponseText || "";
              }

              // Handle "None of the above" option
              let responseData = [];
              if (Array.isArray(questionCopy.Response)) {
                for (let i = 0; i < questionCopy.Response.length; i++) {
                  const item = questionCopy.Response[i];
                  if (item && item.Selected && item.value) {
                    responseData.push(item.value);
                  }
                }
              }

              if (
                questionCopy.IsNoneOfTheAboveEnabled &&
                questionCopy.NoneOfTheAboveText &&
                responseData.includes(questionCopy.NoneOfTheAboveText)
              ) {
                if (languageData) {
                  try {
                    // Create a local copy to avoid modifying the original
                    let langData = languageData;
                    if (typeof langData === 'string') {
                      langData = JSON.parse(langData);
                    }

                    if (Array.isArray(langData)) {
                      const engDataJson = langData.filter(item =>
                        item && item.LanguageCode === 120000000
                      ) || [];

                      let englishChoicesMatch = null;
                      for (let i = 0; i < engDataJson.length; i++) {
                        const item = engDataJson[i];
                        if (item && item.TextCode === questionCopy.NoneOfTheAboveTextCode) {
                          englishChoicesMatch = item;
                          break;
                        }
                      }

                      if (englishChoicesMatch && englishChoicesMatch.TextValue) {
                        englishResponse = englishChoicesMatch.TextValue;
                      }
                    } else {
                      console.error("languageData is not an array:", langData);
                    }
                  } catch (error) {
                    console.error("Error handling languageData:", error);
                  }
                }
              }

              // Handle "Other" option
              if (questionCopy.IsOtherEnabled && questionCopy.OtherResponse) {
                if (englishResponse) {
                  englishResponse += `${questionCopy.FieldDelimiter || ','} ${questionCopy.OtherResponse}`;
                } else {
                  englishResponse = questionCopy.OtherResponse;
                }
              }
            } else {
              // Single select question handling
              let matchedIndex = -1;
              if (Array.isArray(questionCopy.Choices) && questionCopy.Response) {
                for (let i = 0; i < questionCopy.Choices.length; i++) {
                  const choice = questionCopy.Choices[i];
                  if (choice && choice.choiceText === questionCopy.Response) {
                    matchedIndex = i;
                    break;
                  }
                }
              }

              if (matchedIndex !== -1 && matchedIndex < englishtransformedChoices.length) {
                englishResponse = englishtransformedChoices[matchedIndex];
              } else if (questionCopy.ResponseText) {
                englishResponse = questionCopy.ResponseText;
              } else if (questionCopy.OtherResponse) {
                englishResponse = questionCopy.OtherResponse;
              } else {
                englishResponse = "";
              }
            }

            return {
              ...questionCopy,
              Choices: [transformedChoices.join(questionCopy.FieldDelimiter || ',')],
              EnglishResponse: englishResponse,
              formattedChoicesEnglish: englishtransformedChoices,
            };
          }
        ).filter(Boolean); // Filter out any undefined results
      }
    }

    // Add delay before validation for iOS
    await delay();

    // Add safe validation
    try {
      console.log("Data to validate:", finalData);
      const validate = ajv.compile(sourceSchema);
      const valid = validate(finalData);
      console.log("Validation result:", valid);
      if (valid) {
        return finalData;
      } else {
        console.error("Validation failed, errors:", validate.errors);
        return false;
      }
    } catch (error) {
      console.error("Error during validation:", error);
      return false;
    }
  };
  const formatMultichoiceResponse = (response: any) => {
    let finalResponse = Array.isArray(response) ? response : [response];

    var parsedRes = Array.isArray(finalResponse)
      ? finalResponse.map((res: any) => {
        if (res && typeof res === "string") {
          return {
            label: res.trim(),
            Index: res.trim(),
            value: res.trim(),
            Selected: true,
          };
        } else {
          return {
            label: "",
            Index: "",
            value: "",
            Selected: false,
          };
        }
      })
      : [];

    return parsedRes;
  };
  const formatMultichoiceResponseText = (response: any) => {
    var parsedRes = Array.isArray(response) ? response.toString() : "";
    return parsedRes;
  };
  const syncQuestionChanges = (qData: any) => {
    setSourceData((prvState: any) => {
      let updatedSourceData = { ...prvState };
      let updatedSourceDataQuestions = [
        ...updatedSourceData.PatientActivityDetails,
      ];
      let sourceQuestionIndex = updatedSourceDataQuestions.findIndex(
        (q: any) => q.QuestionNumber === qData.questionNumber
      );
      updatedSourceDataQuestions[sourceQuestionIndex].Response =
        qData.responseType == 120000001
          ? formatMultichoiceResponse(qData.response)
          : qData.response;
      updatedSourceDataQuestions[sourceQuestionIndex].ResponseText =
        qData.responseType == 120000001
          ? formatMultichoiceResponseText(qData.response)
          : qData.response;
      updatedSourceDataQuestions[sourceQuestionIndex].OtherResponse =
        qData.othersResponse;
      updatedSourceDataQuestions[sourceQuestionIndex].CompletionStatus =
        qData.completionStatus;

      if (qData.responseType === 120000001) {
        updatedSourceData.PatientActivityDetails = updatedSourceDataQuestions;

        let responseData = qData?.responseCode || "";
        if (responseData && responseData !== qData.noaText) {
          // responseData = responseData
          //   .split(",")
          //   .map(Number)
          //   .sort((a, b) => a - b)
          //   .join(qData.fielddelimiter);
          responseData = responseData.replace(/, /g, qData.fielddelimiter);
        }
        // if (qData.othersResponse) {
        //   if (responseData) {
        //     responseData += qData.fielddelimiter + qData.othersResponse;
        //   } else {
        //     responseData = qData.othersResponse;
        //   }
        // }
        if (
          qData.othersResponse &&
          !responseData.includes(qData.othersResponse)
        ) {
          responseData += `${qData.fielddelimiter}${qData.othersResponse}`;
        }

        updatedSourceDataQuestions[sourceQuestionIndex].ResponseCode =
          responseData;
      } else {
        let responseData = qData?.responseCode || "";
        if (qData.othersResponse) {
          responseData = qData.othersResponse;
        }

        updatedSourceDataQuestions[sourceQuestionIndex].ResponseCode =
          responseData;

        if (qData.response?.includes(qData.othersResponse)) {
          updatedSourceDataQuestions[sourceQuestionIndex].Response = null;
        }
      }
      updatedSourceDataQuestions[sourceQuestionIndex].EnglishChoice =
        qData.englishChoice;
      updatedSourceDataQuestions[sourceQuestionIndex].EnglishResponse =
        qData.englishResponse;
      return updatedSourceData;
    });
  };
  useEffect(() => {
    console.log(sourceData);
  }, [sourceData]);

  return { parseFromSource, parseToSource };
};
